import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "./portfolio.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Footer from "./pages/Footer";
import SkillsContainer from "./pages/Skills";
import Education from "./pages/Education";
import Experience from "./pages/Experience";
import Hero from "./pages/Hero";
import NavBar from "./pages/NavBar";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Project from "./pages/Project";
import Coding from "./pages/Coding";
import Achievements from "./pages/Achievements";

function Portfolio() {
  const [isLoading, setIsLoading] = useState(true);

  // Simulating page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // overflow-x-clip, not overflow-hidden: `hidden` makes this a scroll
  // container, which silently breaks `position: sticky` on the navbar.
  return (
    <div className="scroll-smooth bg-gradient-to-br from-slate-50 to-blue-50 text-gray-800 relative overflow-x-clip">
      {/* The loader fades out over the page rather than cutting to it */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            key="loader"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="text-center">
              <div className="mx-auto mb-4 h-20 w-20 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
              <h2 className="animate-pulse text-xl font-bold text-white">Loading Portfolio…</h2>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Animated background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute w-64 h-64 rounded-full bg-blue-200/20 top-1/4 left-1/4 animate-blob-slow"></div>
        <div className="absolute w-72 h-72 rounded-full bg-purple-200/20 bottom-1/4 right-1/3 animate-blob-medium"></div>
        <div className="absolute w-56 h-56 rounded-full bg-teal-200/20 top-1/2 right-1/4 animate-blob-fast"></div>
      </div>

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Navbar */}
      <NavBar />

      {/* Each section owns its own entrance animation, built on the shared
          variants in ../motion.js */}
      <Hero />
      <About />
      <Coding />
      <SkillsContainer />
      <Project />
      <Achievements />
      <Education />
      <Experience />
      <Contact />
      <Footer />

      {/* Scroll to top button */}
      <ScrollToTop />
    </div>
  );
}

// Scroll to Top Component
function ScrollToTop() {
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowButton(true);
      } else {
        setShowButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <AnimatePresence>
      {showButton && (
        <motion.button
          onClick={scrollToTop}
          aria-label="Scroll back to top"
          className="group fixed bottom-8 right-8 z-50 rounded-full bg-blue-600 p-3 text-white shadow-lg transition-colors duration-300 hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          initial={{ opacity: 0, scale: 0.6, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.6, y: 12 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.94 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transition-transform duration-300 group-hover:-translate-y-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </motion.button>
      )}
    </AnimatePresence>
  );
}

export default Portfolio;